import { useState, useEffect, useRef } from "react";
import {
  Box,
  VStack,
  HStack,
  Text,
  Grid,
  Button,
  Spinner,
  Card,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Wrap,
  WrapItem,
  Tag,
  TagLabel,
} from "@chakra-ui/react";
import { Icon } from "@chakra-ui/react";
import { Upload, X } from "lucide-react";
import { useThemeColors } from "@/hooks/useThemeColors";
import { apiClient } from "@/lib/api/client";
import { useToastNotification } from "@/lib/utils/useToastNotification";
import { Pagination } from "@/components/tables/Pagination";
import type { MetaData } from "@/components/tables/types";

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "image/gif",
  "image/webp",
] as const;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB
const MAX_ATTACHMENT_FILES = 10;
const ACCEPT_ATTR = "image/jpeg,image/png,image/gif,image/webp,.jpeg,.jpg,.png,.gif,.webp";

interface LineItemColumn {
  id: number;
  name: string;
}

interface DownloadSoaFormProps {
  shippingLineId: string;
  shippingLineName: string;
  selectedBookingIds: number[];
  /** Optional: SOA id for fetching details and /soa/{soaId}/download; invalid/missing shows error state */
  soaId?: number;
  onSuccess?: () => void;
  onCancel: () => void;
}

const DOWNLOAD_REVOKE_DELAY_MS = 500;

export function DownloadSoaForm({
  shippingLineId,
  shippingLineName,
  selectedBookingIds,
  soaId,
  onSuccess,
  onCancel,
}: DownloadSoaFormProps) {
  const colors = useThemeColors();
  const { showError, showSuccess } = useToastNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isLoadingLineItems, setIsLoadingLineItems] = useState(false);
  const [lineItemsData, setLineItemsData] = useState<Record<string, unknown>[]>(
    [],
  );
  const [lineItemColumns, setLineItemColumns] = useState<LineItemColumn[]>([]);
  const [lineItemsMeta, setLineItemsMeta] = useState<MetaData | null>(null);
  const [soaDisplay, setSoaDisplay] = useState<{
    dli_sa_number: string;
    work_order: string;
  }>({ dli_sa_number: "", work_order: "" });
  const [finalBookingIds, setFinalBookingIds] =
    useState<number[]>(selectedBookingIds);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const isMountedRef = useRef(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Attachment images for PDF (optional)
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isUploadingAttachments, setIsUploadingAttachments] = useState(false);

  // Guard: soaId must be valid
  const safeSoaId = typeof soaId === "number" && Number.isFinite(soaId) ? soaId : null;

  const hasAttachments = attachmentFiles.length > 0;

  function validateAttachmentFile(file: File): string | null {
    const type = file.type?.toLowerCase() ?? "";
    if (!ALLOWED_IMAGE_TYPES.includes(type as (typeof ALLOWED_IMAGE_TYPES)[number])) {
      return `"${file.name}": must be JPEG, PNG, JPG, GIF, or WebP.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `"${file.name}": must be 5MB or less.`;
    }
    return null;
  }

  function handleAttachmentChange(e: React.ChangeEvent<HTMLInputElement>) {
    setAttachmentError(null);
    const input = e.target;
    const fileList = input.files;
    if (!fileList || fileList.length === 0) return;

    const next: File[] = [];
    const errors: string[] = [];
    const total = Math.min(fileList.length, MAX_ATTACHMENT_FILES + (fileList.length > MAX_ATTACHMENT_FILES ? 1 : 0));
    for (let i = 0; i < fileList.length; i++) {
      if (next.length >= MAX_ATTACHMENT_FILES) {
        errors.push(`Maximum ${MAX_ATTACHMENT_FILES} files allowed.`);
        break;
      }
      const file = fileList[i];
      const err = validateAttachmentFile(file);
      if (err) errors.push(err);
      else next.push(file);
    }
    if (errors.length > 0) {
      setAttachmentError(errors.slice(0, 3).join(" "));
    }
    setAttachmentFiles((prev) => {
      const combined = [...prev, ...next].slice(0, MAX_ATTACHMENT_FILES);
      return combined;
    });
    input.value = "";
  }

  function removeAttachment(index: number) {
    setAttachmentFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachmentError(null);
  }

  // Fetch SOA data for display
  useEffect(() => {
    if (safeSoaId == null) return;

    isMountedRef.current = true;
    const fetchSoaData = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/soa/${safeSoaId}`);
        const soaData = response.data?.data ?? response.data ?? {};
        const bookingIds = soaData.booking_ids ?? [];
        if (isMountedRef.current) {
          setSoaDisplay({
            dli_sa_number: soaData.dli_sa_number ?? "",
            work_order: soaData.work_order ?? "",
          });
          setFinalBookingIds(
            Array.isArray(bookingIds) && bookingIds.length > 0
              ? bookingIds
              : selectedBookingIds,
          );
        }
      } catch (error: unknown) {
        if (isMountedRef.current) {
          showError(error as Error);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      }
    };

    fetchSoaData();
    return () => {
      isMountedRef.current = false;
    };
  }, [safeSoaId, selectedBookingIds, showError]);

  // Fetch line items from /soa/line-items
  useEffect(() => {
    const bookingIdsToUse =
      finalBookingIds.length > 0 ? finalBookingIds : selectedBookingIds;
    if (bookingIdsToUse.length === 0 || !shippingLineId || safeSoaId == null) {
      setLineItemsData([]);
      setLineItemColumns([]);
      setLineItemsMeta(null);
      return;
    }

    const fetchLineItems = async () => {
      setIsLoadingLineItems(true);
      try {
        const response = await apiClient.get("/soa/line-items", {
          params: {
            shipping_line_id: shippingLineId,
            booking_ids: bookingIdsToUse,
            page: currentPage,
            per_page: itemsPerPage,
          },
        });

        const data = response.data?.data ?? [];
        const columns = response.data?.columns ?? [];
        const meta = response.data?.meta ?? null;
        if (isMountedRef.current) {
          setLineItemsData(Array.isArray(data) ? data : []);
          setLineItemColumns(Array.isArray(columns) ? columns : []);
          setLineItemsMeta(meta);
        }
      } catch (error: unknown) {
        if (isMountedRef.current) {
          showError(error as Error);
          setLineItemsData([]);
          setLineItemColumns([]);
          setLineItemsMeta(null);
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoadingLineItems(false);
        }
      }
    };

    fetchLineItems();
  }, [
    currentPage,
    finalBookingIds,
    itemsPerPage,
    selectedBookingIds,
    shippingLineId,
    safeSoaId,
    showError,
  ]);

  const formatCellValue = (value: unknown, columnName: string): string => {
    if (value == null || value === "") return "-";
    const name = String(columnName).toLowerCase();
    const isAmountColumn =
      name.includes("amount") || name.includes("vat") || name === "total";
    if (isAmountColumn && typeof value === "number") {
      return `P ${Number(value).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    }
    if (isAmountColumn && typeof value === "string" && value !== "") {
      return `P ${value}`;
    }
    return String(value);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [finalBookingIds, selectedBookingIds, shippingLineId, safeSoaId]);

  const handlePaginationClick = (url: string | null) => {
    if (!url) return;

    const pageMatch = url.match(/[?&]page=(\d+)/);
    const nextPage = pageMatch ? Number.parseInt(pageMatch[1], 10) : 1;

    setCurrentPage(Math.max(nextPage, 1));
  };

  const handleDownload = async () => {
    if (safeSoaId == null) {
      showError(new Error("SOA ID is required to download."));
      return;
    }

    setIsDownloading(true);
    let objectUrl: string | null = null;
    const includeAttachments = hasAttachments;

    try {
      // When there are attachments, upload first; only proceed to download if upload succeeds
      if (includeAttachments && attachmentFiles.length > 0) {
        setIsUploadingAttachments(true);
        try {
          const formData = new FormData();
          attachmentFiles.forEach((file) => {
            formData.append("images[]", file, file.name);
          });
          await apiClient.request({
            method: "POST",
            url: "/soa-and-billing/attachments",
            data: formData,
            timeout: 60000,
            headers: {
              "Content-Type": undefined,
            },
          });
        } catch (uploadError: unknown) {
          if (isMountedRef.current) {
            showError(uploadError as Error);
          }
          return;
        } finally {
          if (isMountedRef.current) {
            setIsUploadingAttachments(false);
          }
        }
        if (!isMountedRef.current) return;
      }

      const response = await apiClient.get(`/soa/${safeSoaId}/download`, {
        responseType: "blob",
        timeout: 60000,
        params: { include_attachments: includeAttachments },
      });

      const blob = response.data;
      if (!(blob instanceof Blob)) {
        throw new Error("Invalid response: expected a file.");
      }

      objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.setAttribute("download", `${soaDisplay.dli_sa_number || "SOA"}.pdf`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Revoke after delay so the browser has time to start the download (avoids memory leak)
      window.setTimeout(() => {
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      }, DOWNLOAD_REVOKE_DELAY_MS);

      if (isMountedRef.current) {
        showSuccess("Success", "SOA PDF downloaded successfully.");
        onSuccess?.();
      }
    } catch (error: unknown) {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      if (isMountedRef.current) {
        showError(error as Error);
      }
    } finally {
      if (isMountedRef.current) {
        setIsDownloading(false);
        setIsUploadingAttachments(false);
      }
    }
  };

  if (safeSoaId == null) {
    return (
      <Box p={4}>
        <Text color={colors.textSecondary}>Invalid SOA. Cannot load form.</Text>
        <Button mt={4} onClick={onCancel} variant="outline" size="sm">
          Cancel
        </Button>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text mt={4} color={colors.text}>
          Loading SOA data...
        </Text>
      </Box>
    );
  }

  return (
    <Box minW={0} w="100%" maxW="100%">
      <VStack spacing={4} align="stretch">
        {/* Display-only section: labels only */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <Text fontWeight="medium" color={colors.text}>
                  Shipping Line:
                </Text>
                <Text color={colors.text} bg={colors.sectionBg} px={3} py={2} borderRadius="md">
                  {shippingLineName || "-"}
                </Text>
              </Grid>
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <Text fontWeight="medium" color={colors.text}>
                  DLI-SA Number:
                </Text>
                <Text color={colors.text} bg={colors.sectionBg} px={3} py={2} borderRadius="md">
                  {soaDisplay.dli_sa_number || "-"}
                </Text>
              </Grid>
              <Grid
                templateColumns={{ base: "1fr", md: "3fr 9fr" }}
                gap={4}
                alignItems="start"
              >
                <Text fontWeight="medium" color={colors.text}>
                  Work Order Number:
                </Text>
                <Text color={colors.text} bg={colors.sectionBg} px={3} py={2} borderRadius="md">
                  {soaDisplay.work_order || "-"}
                </Text>
              </Grid>
            </VStack>
          </CardBody>
        </Card>

          {/* Table Section: scroll horizontally when wide, keep sidebar visible */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border} minW={0}>
          <CardBody p={0} minW={0}>
          {isLoadingLineItems ? (
            <Box p={8} textAlign="center">
              <Spinner size="lg" color="blue.500" />
              <Text mt={4} color={colors.text}>
                Loading details...
              </Text>
            </Box>
          ) : lineItemsData.length === 0 ? (
            <Box p={8} textAlign="center">
              <Text color={colors.textSecondary}>No records found</Text>
            </Box>
          ) : (
            <Box minW={0} overflowX="auto">
              <TableContainer minW="min-content">
                <Table variant="simple" size="sm">
                <Thead bg={colors.sectionBg}>
                  <Tr>
                    {lineItemColumns.map((col) => {
                      const isNumeric = /amount|vat|total/i.test(col.name);
                      return (
                        <Th
                          key={col.id}
                          borderColor={colors.border}
                          color={colors.text}
                          isNumeric={isNumeric}
                        >
                          {col.name}
                        </Th>
                      );
                    })}
                  </Tr>
                </Thead>
                <Tbody>
                  {lineItemsData.map((row, index) => (
                    <Tr key={index}>
                      {lineItemColumns.map((col) => {
                        const value = row[col.name];
                        const isNumeric = /amount|vat|total/i.test(col.name);
                        return (
                          <Td
                            key={col.id}
                            borderColor={colors.border}
                            color={colors.text}
                            isNumeric={isNumeric}
                          >
                            {formatCellValue(value, col.name)}
                          </Td>
                        );
                      })}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
            </Box>
          )}
          {lineItemsMeta && lineItemsData.length > 0 && (
            <Box
              p={4}
              borderTopWidth="1px"
              borderColor={colors.border}
            >
              <Pagination metas={lineItemsMeta} onClick={handlePaginationClick} />
            </Box>
          )}
          </CardBody>
        </Card>

        {/* Attachments (optional): images for PDF */}
        <Card bg={colors.cardBg} borderWidth="1px" borderColor={colors.border}>
          <CardBody>
            <FormControl isInvalid={!!attachmentError}>
            <VStack align="stretch" spacing={3}>
              <FormLabel fontWeight="medium" color={colors.text}>
                Attachments (optional)
              </FormLabel>
              <FormHelperText color={colors.textSecondary} mt={0}>
                JPEG, PNG, JPG, GIF, or WebP. Max 5MB each. Max {MAX_ATTACHMENT_FILES} files.
              </FormHelperText>
              <HStack spacing={2} flexWrap="wrap">
                <Button
                  size="sm"
                  leftIcon={<Icon as={Upload} boxSize={4} />}
                  onClick={() => fileInputRef.current?.click()}
                  isDisabled={attachmentFiles.length >= MAX_ATTACHMENT_FILES}
                  variant="outline"
                  borderColor={colors.border}
                  color={colors.text}
                >
                  Choose images
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPT_ATTR}
                  multiple
                  onChange={handleAttachmentChange}
                  style={{ display: "none" }}
                  aria-hidden
                />
                {attachmentFiles.length > 0 && (
                  <Text fontSize="sm" color={colors.textSecondary}>
                    {attachmentFiles.length} file(s) selected
                  </Text>
                )}
              </HStack>
              <FormErrorMessage>{attachmentError ?? ""}</FormErrorMessage>
              {attachmentFiles.length > 0 && (
              <Wrap spacing={2}>
                {attachmentFiles.map((file, index) => (
                  <WrapItem key={`${file.name}-${index}`}>
                    <Tag size="md" colorScheme="blue" borderRadius="full" gap={1}>
                      <TagLabel maxW="120px" noOfLines={1} title={file.name}>
                        {file.name}
                      </TagLabel>
                      <IconButton
                        aria-label={`Remove ${file.name}`}
                        size="xs"
                        variant="ghost"
                        icon={<Icon as={X} boxSize={3} />}
                        onClick={() => removeAttachment(index)}
                        minW={6}
                        minH={6}
                      />
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
            </VStack>
            </FormControl>
          </CardBody>
        </Card>

        {/* Action Buttons: Cancel and Download */}
        <HStack spacing={4} justify="flex-end" pt={2}>
          <Button
            variant="outline"
            onClick={onCancel}
            borderColor={colors.border}
            color={colors.text}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDownload}
            colorScheme="blue"
            isLoading={isDownloading}
            loadingText={isUploadingAttachments ? "Uploading..." : "Downloading..."}
            isDisabled={safeSoaId == null}
          >
            Download
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}
